const core = {
  revisionInstructionTooLong: 'Enter revision instructions within {{count}} characters. Your input has been preserved.',
  limitsTitle: 'Input limits',
  limitsClaim: 'Claim: up to {{count}} characters',
  limitsEvidence: 'Evidence: up to {{count}} characters',
  limitsCausalClaims: 'Causal hypotheses: up to {{count}} items',
  limitsInstruction: 'Revision instruction: up to {{count}} characters',
  backToHistory: 'Back to analysis history',
  title: 'DRBFM',
  description: 'Perform failure mode analysis based on design changes',
  newAnalysis: 'New Analysis',
  sessionHistory: 'Analysis History',
  steps: {
      input: 'Input',
      processing: 'Processing',
      results: 'Results',
    },
  input: {
      title: 'Analysis Data Input',
      description: 'Enter the item name and the old/new specification content for design change analysis.',
      basicInfo: 'Basic Information',
      itemName: 'Item Name',
      itemNamePlaceholder: 'e.g., Brake Pad, ECU Board',
      itemNameHelper: 'Enter the name of the item to analyze',
      analysisTitle: 'Analysis Title',
      analysisTitlePlaceholder: 'e.g., 2024 Model Specification Change Analysis',
      analysisTitleHelper: 'Optional title for this analysis',
      specifications: 'Specifications',
      specificationsHelp: 'Enter the old and new specification content. You can type directly or load from a file.',
      oldSpecification: 'Old Specification',
      oldSpecPlaceholder: 'Enter old specification content...\n\nExample:\nMaterial: SUS304\nThickness: 2.0mm\nSurface Treatment: None\nTolerance: ±0.1mm',
      newSpecification: 'New Specification',
      newSpecPlaceholder: 'Enter new specification content...\n\nExample:\nMaterial: SUS316L\nThickness: 1.8mm\nSurface Treatment: Ni Plating\nTolerance: ±0.05mm',
      selectDocument: 'Select from Documents',
      loadFromDocument: 'Load from Document',
      startAnalysis: 'Start Analysis',
      starting: 'Starting analysis...',
      analysisInfo: 'AI will compare specifications, extract changes, and perform failure mode analysis. This may take a few minutes.',
      documentMode: 'Document Selection',
      textMode: 'Text Input',
      selectOldSpec: 'Select Old Specification',
      selectNewSpec: 'Select New Specification',
    },
  documentSelector: {
      selectDocument: 'Select Document',
      searchPlaceholder: 'Search documents...',
      searchHelp: 'Search registered documents by title, description, file name, category, or tag. Register specifications from Documents first if they are not listed.',
      resultsCount: 'Showing {{shown}} of {{total}}',
      loadMore: 'Show more',
      loadingMore: 'Loading...',
      selected: 'Selected',
      change: 'Change',
      fetchError: 'Failed to fetch document list',
      contentError: 'Failed to retrieve document content',
      noResults: 'No search results',
      noDocuments: 'No documents available',
      loadingContent: 'Loading content...',
      documentSelected: 'Document selected',
    },
  history: {
      title: 'Analysis History',
      back: 'Back to DRBFM',
      noSessions: 'No analysis history',
      newAnalysis: 'New Analysis',
      startFirst: 'Start your first analysis',
      columnItem: 'Item',
      columnTitle: 'Title',
      columnAnalysis: 'Analysis Target',
      columnStatus: 'Status',
      columnCreated: 'Created',
      columnCompleted: 'Completed',
      columnActions: 'Actions',
      resume: 'Resume editing',
      viewResults: 'View Results',
      view3D: 'View in 3D',
      export: 'Export',
      delete: 'Delete',
      deleteTitle: 'Delete Analysis Results',
      deleteConfirm: 'Are you sure you want to delete the analysis results for "{{itemName}}"? This action cannot be undone.',
      exportError: 'Export failed',
      statusCompleted: 'Completed',
      statusProcessing: 'Processing',
      statusFailed: 'Failed',
      statusDraft: 'Draft',
      preview: 'Preview',
      exportExcel: 'Export Excel',
      previewTitle: 'DRBFM Analysis Results Preview',
      previewFrameTitle: 'DRBFM Preview',
      previewError: 'Failed to load preview',
      resultsFetchFailed: 'Failed to fetch DRBFM results',
    },
  processing: {
    flowLabel: 'Flow {{id}}: {{label}}',
      title: 'Analysis in Progress',
      description: 'AI is analyzing the design changes. Please wait for completion.',
      analyzing: 'Analyzing...',
      pleaseWait: 'Please wait',
      overallProgress: 'Overall Progress',
      flowStatus: 'Flow Status',
      progressLog: 'Progress Log',
      creatingSession: 'Creating session...',
      flow1: {
        title: 'Change Extraction',
        description: 'Comparing old and new specifications to extract changes',
      },
      flow2: {
        title: 'Knowledge Base Search',
        description: 'Searching past troubles and design standards for related information',
      },
      flow3: {
        title: 'Failure Mode Analysis',
        description: 'Identifying potential failure modes related to changes',
      },
      flow4: {
        title: 'Countermeasure Generation',
        description: 'Generating effective countermeasures for failure modes',
      },
      flow5: {
        title: 'Report Generation',
        description: 'Generating DRBFM table and report',
      },
      pending: 'Pending',
      inProgress: 'In Progress',
      completed: 'Completed',
      failed: 'Failed',
      cancel: 'Cancel Analysis',
    },
  results: {
      title: 'Analysis Results',
      description: 'View detailed DRBFM analysis results.',
      summary: 'Summary',
      designChanges: 'Design Changes',
      failureModes: 'Failure Modes',
      countermeasures: 'Countermeasures',
      exportHTML: 'Export as HTML',
      exportPDF: 'Export as PDF',
      export: 'Export',
      exporting: 'Exporting...',
      newAnalysis: 'New Analysis',
      startNew: 'Start New Analysis',
      backToInput: 'Back to Input',
      noResults: 'No analysis results',
      tabsAria: 'DRBFM results tabs',
    },
  table: {
      category: 'Category',
      item: 'Item',
      itemName: 'Item Name',
      oldSpec: 'Old Specification',
      newSpec: 'New Specification',
      specifications: 'Specification Comparison',
      change: 'Change Description',
      changeDescription: 'Change Description',
      impact: 'Impact Areas',
      impactAreas: 'Impact Areas',
      failureMode: 'Failure Mode',
      failureDescription: 'Failure Mode',
      cause: 'Cause',
      failureCause: 'Failure Cause',
      effect: 'Effect',
      failureEffect: 'Failure Effect',
      severity: 'Severity',
      severityScore: 'Score',
      rpn: 'RPN',
      rpnScore: 'RPN',
      countermeasure: 'Countermeasure',
      countermeasureType: 'Type',
      type: 'Type',
      description: 'Description',
      effectiveness: 'Effectiveness',
      priority: 'Priority',
      reference: 'Reference',
      designChanges: 'Design Changes',
      failureModes: 'Failure Modes',
      countermeasures: 'Countermeasures',
      noData: 'No data available',
    },
  severity: {
      critical: 'Critical',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
    },
  countermeasureType: {
      prevention: 'Prevention',
      detection: 'Detection',
      mitigation: 'Mitigation',
    },
  category: {
      Material: 'Material',
      Dimension: 'Dimension',
      Process: 'Process',
      Structure: 'Structure',
      Surface: 'Surface',
      Tolerance: 'Tolerance',
      Function: 'Function',
      Other: 'Other',
    },
  impactArea: {
      Design: 'Design',
      Manufacturing: 'Manufacturing',
      Quality: 'Quality',
      Safety: 'Safety',
      Cost: 'Cost',
      Maintenance: 'Maintenance',
    },
  status: {
      draft: 'Draft',
      analyzing: 'Analyzing',
      completed: 'Completed',
      failed: 'Failed',
      pending: 'Pending',
      inProgress: 'In Progress',
    },
  stats: {
    criticalRisks: 'Critical risks',
    preventionActions: 'Prevention actions',
    detectionActions: 'Detection actions',
    mitigationActions: 'Mitigation actions',
      designChanges: 'Design Changes',
      failureModes: 'Failure Modes',
      countermeasures: 'Countermeasures',
      critical: 'Critical: {{count}}',
      highPriority: 'High Priority: {{count}}',
    },
};

export default core;
